import { Plus, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  ehPorAcoes,
  ESPECIES_ACAO,
  NOME_ESPECIE_ACAO,
  nomeDe,
  termoDaUnidade,
  type EspecieAcao,
  type PapelSocioComandita,
  type Pessoa,
} from '@social/shared';
import { Botao, Campo, Entrada, Selecao } from '../componentes/ui';
import {
  moedaBR,
  TIPOS_COM_PAPEL_DE_SOCIO,
  type RascunhoSociedade,
} from './sociedade-nova-comum';
import { LinhaCheia, Secao } from './sociedade-nova-secao';

/*
  As duas seções de ARRAY do cadastro de sociedade, mais o `Poder`.

  Saíram do arquivo da página porque são as que crescem: cada regra nova de
  sócio, ou cada poder novo de administrador, entra aqui — e a página passou
  das 600 linhas do lint na primeira vez que as duas conviveram com o resto.

  Recebem o rascunho INTEIRO e uma função `mudar`, em vez de ter estado
  próprio: quotas e capital se calculam entre si, e dois estados separados
  divergiriam no primeiro campo editado.
*/

export function SecaoSocios({
  rascunho: r,
  mudar,
  pessoas,
  capital,
  valorNominal,
  minimo,
}: {
  rascunho: RascunhoSociedade;
  mudar: (p: Partial<RascunhoSociedade>) => void;
  pessoas: Pessoa[];
  capital: number;
  valorNominal: number;
  minimo: number;
}): JSX.Element {
  const mexer = (i: number, parcial: Partial<RascunhoSociedade['socios'][number]>) =>
    mudar({
      socios: r.socios.map((s, j) => (j === i ? { ...s, ...parcial } : s)),
    });

  const totalQuotas = r.socios.reduce((t, s) => t + (Number(s.quotas) || 0), 0);
  const temPapel = TIPOS_COM_PAPEL_DE_SOCIO.includes(r.tipo);
  /*
    Numa companhia o vocabulário inteiro muda: acionista, ação, estatuto.
    Chamar de "quota" o que o estatuto chama de "ação" faria a tela dizer uma
    coisa e o documento gerado dizer outra — e quem confere é a Junta.
  */
  const porAcoes = ehPorAcoes(r.tipo);
  const termo = termoDaUnidade(r.tipo);
  const unidade = termo.plural.charAt(0).toUpperCase() + termo.plural.slice(1);
  // Papel (comandita) e espécie (companhia) nunca coexistem — são tipos
  // societários diferentes —, então a coluna extra é a mesma.
  const colunaExtra = temPapel || porAcoes;

  /*
    "Selecione…" tem de REMOVER a chave, não gravá-la como `undefined`.

    Com `exactOptionalPropertyTypes`, `{ papelSocio: undefined }` não é o
    mesmo que a ausência do campo — e a diferença chega ao servidor: o Zod
    veria a chave presente com valor inválido em vez de um campo opcional
    ausente.
  */
  /** Mesma regra do papel: "" REMOVE a chave, não grava `undefined`. */
  const mexerEspecie = (i: number, valor: string) =>
    mudar({
      socios: r.socios.map((s, j) => {
        if (j !== i) return s;
        if (valor === '') {
          const { especie: _fora, classe: _foraClasse, ...semEspecie } = s;
          return semEspecie;
        }
        // Classe só existe em preferencial: trocar para ordinária tem de levar
        // a classe junto, senão o estatuto sairia com "ordinária classe A".
        if (valor !== 'PREFERENCIAL') {
          const { classe: _foraClasse, ...semClasse } = s;
          return { ...semClasse, especie: valor as EspecieAcao };
        }
        return { ...s, especie: valor as EspecieAcao };
      }),
    });

  const mexerPapel = (i: number, valor: string) =>
    mudar({
      socios: r.socios.map((s, j) => {
        if (j !== i) return s;
        if (valor === '') {
          const { papelSocio: _fora, ...semPapel } = s;
          return semPapel;
        }
        return { ...s, papelSocio: valor as PapelSocioComandita };
      }),
    });

  return (
    <Secao
      titulo={porAcoes ? 'Capital e acionistas' : 'Capital e sócios'}
      descricao={
        porAcoes
          ? `A companhia exige no mínimo ${minimo} acionistas. O capital é a soma das ações — não se digita.`
          : `Este tipo exige ${minimo === 1 ? 'exatamente um sócio' : `no mínimo ${minimo} sócios`}. O capital é a soma das quotas — não se digita.`
      }
    >
      <Campo
        rotulo={`Valor nominal da ${termo.singular} (R$)`}
        id="valorNominalQuota"
        obrigatorio
        dica={
          porAcoes
            ? 'Zero = ação sem valor nominal (art. 11 da Lei 6.404/76).'
            : 'Quase sempre R$ 1,00'
        }
      >
        <Entrada
          id="valorNominalQuota"
          inputMode="decimal"
          value={r.valorNominalQuota}
          onChange={(e) => mudar({ valorNominalQuota: e.target.value })}
        />
      </Campo>

      {/*
        O capital calculado, e não um campo.

        É a regra `capital-nao-bate` do validador virando desenho de tela: em
        vez de deixar a pessoa digitar um número que o laudo depois recusa, o
        número aparece pronto e ela ajusta as quotas até ficar o que quer.
      */}
      <div className="border-tinta-100 bg-superficie-2 flex flex-col justify-center rounded-lg border px-3 py-2">
        <span className="text-tinta-500 text-xs">Capital social</span>
        <output className="text-tinta-900 text-lg font-semibold tabular-nums">
          {moedaBR(capital)}
        </output>
        <span className="text-tinta-500 text-xs tabular-nums">
          {totalQuotas.toLocaleString('pt-BR')} {termo.plural} × {moedaBR(valorNominal)}
        </span>
      </div>

      <LinhaCheia>
        <div className="flex flex-col gap-3">
          {r.socios.map((s, i) => (
            <div
              key={i}
              className={
                colunaExtra
                  ? 'border-tinta-100 grid grid-cols-1 items-end gap-3 rounded-lg border p-3 sm:grid-cols-[2fr_1.5fr_1fr_1fr_auto]'
                  : 'border-tinta-100 grid grid-cols-1 items-end gap-3 rounded-lg border p-3 sm:grid-cols-[2fr_1fr_1fr_auto]'
              }
            >
              <Campo
                rotulo={`${porAcoes ? 'Acionista' : 'Sócio'} ${i + 1}`}
                id={`socio-${i}`}
                obrigatorio
              >
                <Selecao
                  id={`socio-${i}`}
                  value={s.pessoaId}
                  onChange={(e) => mexer(i, { pessoaId: e.target.value })}
                >
                  <option value="">Selecione…</option>
                  {pessoas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {nomeDe(p)}
                    </option>
                  ))}
                </Selecao>
              </Campo>

              {/*
                O papel só existe na Comandita Simples, e ali ele é a essência
                do tipo: o comanditado responde ilimitadamente e administra; o
                comanditário responde até o valor da quota e NÃO pode
                administrar (arts. 1.045 e 1.047 CC). Sem declarar quem é quem,
                o validador reprova — e com razão, porque o contrato não teria
                como dizer quem responde pelo quê.
              */}
              {temPapel && (
                <Campo rotulo="Papel na comandita" id={`papel-${i}`} obrigatorio>
                  <Selecao
                    id={`papel-${i}`}
                    value={s.papelSocio ?? ''}
                    onChange={(e) => mexerPapel(i, e.target.value)}
                  >
                    <option value="">Selecione…</option>
                    <option value="COMANDITADO">Comanditado (administra)</option>
                    <option value="COMANDITARIO">Comanditário (não administra)</option>
                  </Selecao>
                </Campo>
              )}

              {/*
                A espécie define o DIREITO DE VOTO: preferencial sem voto não
                pode passar de metade das ações (art. 15, §2º), e a vantagem
                que a torna preferencial é declarada no estatuto (art. 17).
                Ausente = ordinária, que é o padrão da lei.
              */}
              {porAcoes && (
                <Campo rotulo="Espécie" id={`especie-${i}`}>
                  <Selecao
                    id={`especie-${i}`}
                    value={s.especie ?? ''}
                    onChange={(e) => mexerEspecie(i, e.target.value)}
                  >
                    <option value="">Ordinária</option>
                    {ESPECIES_ACAO.map((e) => (
                      <option key={e} value={e}>
                        {NOME_ESPECIE_ACAO[e]}
                      </option>
                    ))}
                  </Selecao>
                </Campo>
              )}

              <Campo rotulo={unidade} id={`quotas-${i}`} obrigatorio>
                <Entrada
                  id={`quotas-${i}`}
                  inputMode="numeric"
                  value={s.quotas}
                  onChange={(e) => {
                    // Integralizadas acompanham as quotas enquanto a pessoa não
                    // as separa: integralização à vista é o caso comum, e
                    // repetir o número em dois campos é trabalho sem escolha.
                    const acompanhava = s.integralizadas === s.quotas;
                    mexer(i, {
                      quotas: e.target.value,
                      ...(acompanhava ? { integralizadas: e.target.value } : {}),
                    });
                  }}
                />
              </Campo>

              <Campo
                rotulo="Integralizadas"
                id={`integralizadas-${i}`}
                obrigatorio
                dica="Igual às quotas quando à vista"
              >
                <Entrada
                  id={`integralizadas-${i}`}
                  inputMode="numeric"
                  value={s.integralizadas}
                  onChange={(e) => mexer(i, { integralizadas: e.target.value })}
                />
              </Campo>

              <Botao
                variante="fantasma"
                tamanho="icone"
                disabled={r.socios.length <= minimo}
                title={
                  r.socios.length <= minimo
                    ? `Este tipo exige ${minimo} sócio(s)`
                    : 'Remover este sócio'
                }
                aria-label={`Remover sócio ${i + 1}`}
                onClick={() => mudar({ socios: r.socios.filter((_, j) => j !== i) })}
              >
                <Trash2 className="size-4" aria-hidden />
              </Botao>
            </div>
          ))}

          <div>
            <Botao
              variante="secundaria"
              disabled={r.tipo === 'SLU'}
              title={
                r.tipo === 'SLU'
                  ? 'Sociedade Limitada Unipessoal tem exatamente um sócio'
                  : undefined
              }
              onClick={() =>
                mudar({
                  socios: [...r.socios, { pessoaId: '', quotas: '', integralizadas: '' }],
                })
              }
            >
              <Plus className="size-4" aria-hidden />
              Adicionar sócio
            </Botao>
          </div>
        </div>
      </LinhaCheia>
    </Secao>
  );
}

export function SecaoAdministradores({
  rascunho: r,
  mudar,
  pessoas,
}: {
  rascunho: RascunhoSociedade;
  mudar: (p: Partial<RascunhoSociedade>) => void;
  pessoas: Pessoa[];
}): JSX.Element {
  const mexer = (
    i: number,
    parcial: Partial<RascunhoSociedade['administradores'][number]>,
  ) =>
    mudar({
      administradores: r.administradores.map((a, j) =>
        j === i ? { ...a, ...parcial } : a,
      ),
    });

  return (
    <Secao
      titulo="Administração"
      descricao="Sem administrador vigente ninguém representa a sociedade, e o contrato não se registra (art. 1.060 do Código Civil)."
    >
      <LinhaCheia>
        <div className="flex flex-col gap-3">
          {r.administradores.map((a, i) => (
            <div key={i} className="border-tinta-100 rounded-lg border p-3">
              <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_auto]">
                <Campo rotulo={`Administrador ${i + 1}`} id={`adm-${i}`} obrigatorio>
                  <Selecao
                    id={`adm-${i}`}
                    value={a.pessoaId}
                    onChange={(e) => mexer(i, { pessoaId: e.target.value })}
                  >
                    <option value="">Selecione…</option>
                    {pessoas.map((p) => (
                      <option key={p.id} value={p.id}>
                        {nomeDe(p)}
                      </option>
                    ))}
                  </Selecao>
                </Campo>

                <Botao
                  variante="fantasma"
                  tamanho="icone"
                  disabled={r.administradores.length <= 1}
                  title={
                    r.administradores.length <= 1
                      ? 'A sociedade precisa de ao menos um administrador'
                      : 'Remover este administrador'
                  }
                  aria-label={`Remover administrador ${i + 1}`}
                  onClick={() =>
                    mudar({
                      administradores: r.administradores.filter((_, j) => j !== i),
                    })
                  }
                >
                  <Trash2 className="size-4" aria-hidden />
                </Botao>
              </div>

              {/*
                Os poderes são caixas de seleção e não um seletor: cada um vira
                uma ORAÇÃO do contrato, e são independentes entre si. Os
                padrões são os do contrato mais comum — quem precisa de outro
                arranjo desmarca.
              */}
              <div className="mt-3 flex flex-col gap-2">
                <Poder
                  id={`iso-${i}`}
                  marcado={a.usoIsoladoDoNome}
                  aoMudar={(v) => mexer(i, { usoIsoladoDoNome: v })}
                >
                  Pode usar o nome empresarial isoladamente
                </Poder>
                <Poder
                  id={`fianca-${i}`}
                  marcado={a.vedadoFiancaAvalEndosso}
                  aoMudar={(v) => mexer(i, { vedadoFiancaAvalEndosso: v })}
                >
                  Vedado prestar fiança, aval ou endosso de favor
                </Poder>
                <Poder
                  id={`proc-${i}`}
                  marcado={a.podeConstituirProcuradores}
                  aoMudar={(v) => mexer(i, { podeConstituirProcuradores: v })}
                >
                  Pode constituir procuradores
                </Poder>
              </div>
            </div>
          ))}

          <div>
            <Botao
              variante="secundaria"
              onClick={() =>
                mudar({
                  administradores: [
                    ...r.administradores,
                    {
                      pessoaId: '',
                      usoIsoladoDoNome: true,
                      vedadoFiancaAvalEndosso: true,
                      podeConstituirProcuradores: true,
                    },
                  ],
                })
              }
            >
              <Plus className="size-4" aria-hidden />
              Adicionar administrador
            </Botao>
          </div>
        </div>
      </LinhaCheia>
    </Secao>
  );
}

function Poder({
  id,
  marcado,
  aoMudar,
  children,
}: {
  id: string;
  marcado: boolean;
  aoMudar: (v: boolean) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <label htmlFor={id} className="text-tinta-700 flex items-center gap-2 text-sm">
      <input
        id={id}
        type="checkbox"
        checked={marcado}
        onChange={(e) => aoMudar(e.target.checked)}
        className="accent-marca-600 size-4"
      />
      {children}
    </label>
  );
}

/**
 * O rascunho vira DTO uma vez só, no envio.
 *
 * As quantias saem de texto para centavos aqui, e não em cada campo: espalhar
 * a conversão faria cada `onChange` decidir sozinho como arredondar, e a
 * aritmética do validador não perdoa um centavo.
 */
